import type { EmojiOption } from "../components/common/EmojiPicker";
import { TAG_EMOJIS } from "../components/appointmentTags/tagEmojis";
import { BusinessInfo } from "../types/context";

/**
 * Checkout reminder model — the salon-configured note staff see below the Total
 * in the transaction modal and on the checkout screen. Configured in
 * Settings → Checkout → Checkout Reminders and stored on the salon record
 * (`checkout_reminder_*`, see `BusinessInfo`). Always on: it shows whenever
 * the message is non-blank; clearing the message removes it.
 */

export const CHECKOUT_REMINDER_TEXT_MAX = 500;
export const CHECKOUT_REMINDER_TITLE_MAX = 40;
export const DEFAULT_CHECKOUT_REMINDER_TITLE = "Before checkout";

/** Reminder-specific emojis, listed first in the picker. */
const REMINDER_FIRST_EMOJIS: EmojiOption[] = [
  { emoji: "\u{1F5E8}\u{FE0F}", label: "Chat" },
  { emoji: "\u{1F4AC}", label: "Speech bubble" },
  { emoji: "\u{1F4F8}", label: "Camera" },
  { emoji: "\u{2753}", label: "Question" },
  { emoji: "\u{1F4B5}", label: "Money" },
];

/**
 * Emojis offered by the reminder picker: the reminder-specific ones, then the
 * rest of the appointment tag set (no duplicates).
 */
export const CHECKOUT_REMINDER_EMOJIS: EmojiOption[] = [
  ...REMINDER_FIRST_EMOJIS,
  ...TAG_EMOJIS.filter(
    (tag) => !REMINDER_FIRST_EMOJIS.some((first) => first.emoji === tag.emoji),
  ),
];

/** A reminder ready to render. `emoji: null` = the default icon. */
export interface CheckoutReminder {
  title: string;
  text: string;
  emoji: string | null;
}

/**
 * The single "should the reminder show" rule: the message is non-blank.
 * Applies the title fallback. Returns null when nothing should render.
 */
export const getCheckoutReminder = (
  businessInfo: Partial<BusinessInfo> | null | undefined,
): CheckoutReminder | null => {
  const text = businessInfo?.checkout_reminder_text?.trim();
  if (!text) return null;
  return {
    title:
      businessInfo?.checkout_reminder_title?.trim() ||
      DEFAULT_CHECKOUT_REMINDER_TITLE,
    text,
    emoji: businessInfo?.checkout_reminder_emoji?.trim() || null,
  };
};
